import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import qs from 'qs';
import { isEmpty, isEqual, get } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import produce from 'immer';
import Countdown, { zeroPad } from 'react-countdown';
import { apiResponseCodes, regex, intervalToResendOtpInSec } from '../../../../constants/globals';
import { constants } from '../../../../constants/configuration';
import resourcesConfig from '../../../../resources';
import { rewardSignUpStates, rewardCategory } from '../../../Rewards/config';
import rewardsRoutes from '../../../../routes/rewards';
import pagesRoutes from '../../../../routes/pages';
import asyncRoute from '../../../../helpers/hoc/asyncRoute';
import { getTimeInterval, isDateInvalid } from '../../../../utils/fudrLocalizationHelper';
import { dateFormats } from '../../../../utils/dateFormats';
import { handleLinkUrl, safeDecode, sanitizeValue } from '../../../../utils/helpers';
import { checkExisitingRewardGuestApi } from '../../../../api/api';
import {
  clearApiErrorResponseAction,
  beginAjaxCallAction,
  endAjaxCallAction,
} from '../../../../actions/common';
import { sendOtpAction, attemptLoginAction } from '../../../../actions/app.action';
import {
  updateRewardGuestProfileAction,
  fetchRewardGuestDetailsAction,
  dispatchCallbackUrlAction,
  updateSignupDetailsAction,
  fetchDealByIdAction,
} from '../../../../actions/rewards';

import './Signup.scss';

const TextField = asyncRoute(() => import('../../../../components/MaterialElements/TextField'));
const Button = asyncRoute(() => import('../../../../components/MaterialElements/Button'));
const ErrorComponent = asyncRoute(() => import('../../../../components/ErrorComponent'));
const InvisibleRecaptcha = asyncRoute(() => import('../../../../components/InvisibleRecaptcha'));
const RecaptchaBranding = asyncRoute(() => import('../../../Rewards/components/RecaptchaBranding'));
const GetStarted = asyncRoute(() => import('../../../Rewards/components/AuthGetStarted'));
const Icons = asyncRoute(() => import('../../../../components/Icons'));
const SignupDetails = asyncRoute(() => import('../Popup/SignupDetails'));

const nameValidatorPattern = new RegExp(regex.nameValidator);
const emailValidatorPattern = new RegExp(regex.emailValidator);

const Signup = ({
  history,
  guestDetails,
  errorResponse,
  actions,
  location,
  appStartupInfo,
  callbackUrl,
  signupDetails,
  showRewardProgramTitle,
  rewardProgram,
  themeData,
  staticPages,
  signUpRequiredFields,
  signUpHiddenFields,
  socialMediaCampaignDetails,
  genderFields,
}) => {
  let [currentState, setCurrentState] = useState(rewardSignUpStates.enterNumber);
  const [phoneNumber, setPhoneNumber] = useState();
  const [otp, setOTP] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [anniversaryDate, setAnniversaryDate] = useState('');
  const [gender, setGender] = useState('');
  const [resendOtpDuration, setResendOtpDuration] = useState();

  const [isInvalid, setIsInvalid] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const [showInvalidOtp, setShowInvalidOtp] = useState(false);
  const [guestId, setGuestId] = useState('');
  const [showSignupDetailsPopup, setShowSignupDetailsPopup] = useState(false);
  const parsed = qs.parse(location.search, { ignoreQueryPrefix: true });

  const recaptchaRef = useRef(null);
  const hasTiers = get(rewardProgram, 'hasTiers', false);
  const intl = useIntl();
  const isAnniversaryTypeReward = hasTiers
    ? get(rewardProgram, 'tiers', []).some((tier) =>
        get(tier, 'redeemRules', []).some((rule) => isEqual(rule.type, rewardCategory.anniversary)),
      )
    : get(rewardProgram, 'redeemRules', []).some(
        (element) => element.type === rewardCategory.anniversary,
      );
  const userName = get(parsed, 'userName');
  const dealTitle = get(parsed, 'dealTitle', '');
  const dealImg = get(parsed, 'dealImg', '');
  const igUserId = get(parsed, 'userId', '');
  const igUserBusinessId = get(parsed, 'userBusinessId', '');
  const actionState = get(location, 'state');
  const prefilledData = get(actionState, 'prefilledData', {});

  const showUserInfoFields = get(socialMediaCampaignDetails, 'campaignPlan.showUserInfoFields', []);

  useEffect(() => {
    switch (actionState) {
      case 'sign-up':
        setCurrentState(rewardSignUpStates.enterNumber);
        break;
      case 'verify':
        setCurrentState(rewardSignUpStates.verifyNumber);
        break;
      case 'details':
        setCurrentState(rewardSignUpStates.enterDetails);
        break;
      default:
        break;
    }
    rehydrateSignupState();

    return () => {
      recaptchaRef.current = null;
      actions.updateSignupDetails({});
    };
  }, []);

  useEffect(() => {
    const prefilledNumber = get(prefilledData, 'primaryPhoneNumber', '');
    if (!isEmpty(prefilledNumber)) {
      setPhoneNumber(prefilledNumber);
    }
  }, [prefilledData]);

  useEffect(() => {
    // const dealCode = get(socialMediaCampaignDetails, 'campaignPlan.rules.campaignDeals.0', '');
    // if (!isEmpty(dealCode)) {
    //   actions.fetchDealById({ dealCode }).then((response) => {
    //     if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
    //       setName(userName);
    //     }
    //   });
    // }
    setName(userName);
  }, [socialMediaCampaignDetails]);

  useEffect(() => {
    if (!isEmpty(errorResponse) && isEqual(get(errorResponse, 'code'), 'OTP_INVALID')) {
      setShowInvalidOtp(true);
    }
  }, [errorResponse]);

  const rehydrateSignupState = () => {
    const { phoneNumber, name, email, birthDate, anniversaryDate } = signupDetails;
    setName(name);
    setEmail(email);
    setBirthDate(birthDate);
    setPhoneNumber(phoneNumber);
    setAnniversaryDate(anniversaryDate);
  };

  const toggleFieldInvalidationState = ({ name, errorMessage, inValid }) => {
    if (inValid) {
      setIsInvalid((prev) => {
        const updated = { ...prev, [name]: errorMessage };
        return updated;
      });
    } else if (isInvalid.hasOwnProperty(name)) {
      setIsInvalid((prev) =>
        produce(prev, (draft) => {
          delete draft[name];
        }),
      );
    }
  };

  // Utility function for sanitization and validation
  const validatePhoneNumber = (rawValue) => {
    // Remove spaces first
    let sanitizedValue = rawValue.replace(/\s+/g, '');
    // Only remove the country code if overall length is greater than 10 digits
    if (sanitizedValue.length > 10) {
      if (
        (sanitizedValue.startsWith('+91') && sanitizedValue.length === 13) ||
        (sanitizedValue.startsWith('91') && sanitizedValue.length === 12)
      ) {
        sanitizedValue = sanitizedValue.replace(/^(\+?91)/, '');
      }
    }
    return sanitizedValue;
  };

  const updateValidationState = (value) => {
    // Perform the final check against the 10-digit requirement
    if (!value || value.length < 10) {
      toggleFieldInvalidationState({
        name: 'phoneNumber',
        inValid: true,
        errorMessage: (
          <FormattedMessage
            id="invalidPhoneNumberMessage"
            defaultMessage={resourcesConfig.invalidPhoneNumberMessage}
          />
        ),
      });
    } else if (value.length !== 10) {
      toggleFieldInvalidationState({
        name: 'phoneNumber',
        inValid: true,
        errorMessage: (
          <FormattedMessage
            id="invalidPhoneNumberMessage"
            defaultMessage={resourcesConfig.invalidPhoneNumberMessage}
          />
        ),
      });
    } else {
      console.log('Valid phone number:', value);
      toggleFieldInvalidationState({
        name: 'phoneNumber',
        inValid: false,
        errorMessage: '',
      });
    }
  };

  const handleOnChange = (event) => {
    const { name, value } = event.target;
    switch (name) {
      case 'phoneNumber':
        const sanitizedValue = validatePhoneNumber(value);
        // Update the phone number state regardless
        setPhoneNumber(sanitizedValue);

        return;
      case 'otp':
        setOTP(value);
        return;
      case 'name':
        setName(value);
        return;
      case 'email':
        setEmail(value);
        return;
      case 'birthDate':
        setBirthDate(value);
        return;
      case 'anniversaryDate':
        setAnniversaryDate(value);
        return;
      case 'gender':
        setGender(value);
        toggleFieldInvalidationState({
          name,
          inValid: false,
          errorMessage: '',
        });
        return;
      default:
        return;
    }
  };

  const handleOnBlur = (event) => {
    const { name, value } = event.target;

    switch (name) {
      case 'phoneNumber':
        const sanitizedValue = validatePhoneNumber(value);
        updateValidationState(sanitizedValue);
        return;

      case 'otp':
        if (isEmpty(value)) {
          toggleFieldInvalidationState({
            name,
            inValid: true,
            errorMessage: (
              <FormattedMessage
                id="emptyOtpMessage"
                defaultMessage={resourcesConfig.emptyOtpMessage}
              />
            ),
          });
          return;
        }
        break;
      case 'name':
        if (signUpRequiredFields.includes(name) && isEmpty(value)) {
          toggleFieldInvalidationState({
            name,
            inValid: true,
            errorMessage: (
              <FormattedMessage
                id="emptyNameMessage"
                defaultMessage={resourcesConfig.emptyNameMessage}
              />
            ),
          });
          return;
        }

        if (!isEmpty(value) && !nameValidatorPattern.test(value.trim())) {
          toggleFieldInvalidationState({
            name,
            inValid: true,
            errorMessage: (
              <FormattedMessage
                id="invalidNameMessage"
                defaultMessage={resourcesConfig.invalidNameMessage}
              />
            ),
          });
          return;
        }

        setName(value.trim());
        break;

      case 'email':
        if (signUpRequiredFields.includes(name) && isEmpty(value)) {
          toggleFieldInvalidationState({
            name,
            inValid: true,
            errorMessage: (
              <FormattedMessage
                id="emptyEmailMessage"
                defaultMessage={resourcesConfig.emptyEmailMessage}
              />
            ),
          });
          return;
        }

        if (!isEmpty(value) && !emailValidatorPattern.test(value.trim())) {
          toggleFieldInvalidationState({
            name,
            inValid: true,
            errorMessage: (
              <FormattedMessage
                id="invalidEmailMessage"
                defaultMessage={resourcesConfig.invalidEmailMessage}
              />
            ),
          });
          return;
        }

        setEmail(value.trim());
        break;

      case 'birthDate':
        if (signUpRequiredFields.includes('dob') && isEmpty(value)) {
          toggleFieldInvalidationState({
            name,
            inValid: true,
            errorMessage: (
              <FormattedMessage
                id="emptyDOBMessage"
                defaultMessage={resourcesConfig.emptyDOBMessage}
              />
            ),
          });
          return;
        }

        if (!isEmpty(value) && isDateInvalid(value)) {
          toggleFieldInvalidationState({
            name,
            inValid: true,
            errorMessage: (
              <FormattedMessage
                id="invalidDateOfBirthMessage"
                defaultMessage={resourcesConfig.invalidDateOfBirthMessage}
              />
            ),
          });
          return;
        }

        break;
      case 'anniversaryDate':
        if (signUpRequiredFields.includes('doa') && isEmpty(value)) {
          toggleFieldInvalidationState({
            name,
            inValid: true,
            errorMessage: (
              <FormattedMessage
                id="emptyAnniversaryDateMessage"
                defaultMessage={resourcesConfig.emptyAnniversaryDateMessage}
              />
            ),
          });
          return;
        }

        if (!isEmpty(value) && isDateInvalid(value)) {
          toggleFieldInvalidationState({
            name,
            inValid: true,
            errorMessage: (
              <FormattedMessage
                id="invalidDateOfAnniversaryMessage"
                defaultMessage={resourcesConfig.invalidDateOfAnniversaryMessage}
              />
            ),
          });
          return;
        }

        break;
      default:
        return;
    }
    toggleFieldInvalidationState({ name, inValid: false });
  };

  const handleSendOTP = async () => {
    const token = await recaptchaRef.current.executeAsync();
    actions
      .sendOtp(phoneNumber, constants.defaultCountryCode, token, get(appStartupInfo, 'brandId', ''))
      .then((response) => {
        if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
          setResendOtpDuration(getTimeInterval(intervalToResendOtpInSec));
        }
      });
  };

  const handleSubmitSignUpAction = () => {
    const campaignCode = get(socialMediaCampaignDetails, 'campaignCode', '');
    const campaignId = get(socialMediaCampaignDetails, 'id', '');
    const additionalCampaignBody = {
      sourceCode: campaignCode,
      source: campaignId,
    };
    const requestBody = {
      brandId: get(appStartupInfo, 'brandId', ''),
      guestId: get(guestDetails, 'id', ''),
      brandName: get(appStartupInfo, 'brand.title', ''),
      primaryMobileNumber: phoneNumber,
      countryCode: '91',
      isUserConsent: true,
      name,
      rewardJoiningDate: new Date(),
      igUserId,
      igUserBusinessId,
      igUserJoiningDate: new Date(),
      ...(!isEmpty(birthDate) && { dob: birthDate.format(dateFormats.ISO) }),
      ...(!isEmpty(anniversaryDate) && { doa: anniversaryDate.format(dateFormats.ISO) }),
      ...(!isEmpty(email) && { email }),
      ...(!isEmpty(gender) && { gender }),
      ...(!isEmpty(additionalCampaignBody) && additionalCampaignBody),
    };
    const withLoader = false;
    const sendEventToNative = true;
    actions
      .updateRewardGuestProfile(requestBody, withLoader, sendEventToNative)
      .then((response) => {
        if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
          actions.fetchRewardGuestDetails({
            guestId: get(response, 'data.id', ''),
            depth: hasTiers,
          });

          if (callbackUrl) {
            actions.dispatchCallbackUrl({ callbackUrl: '' });
            history.push(callbackUrl);
          } else {
            setShowSignupDetailsPopup(false);
            const searchParams = new URLSearchParams(location.search);
            searchParams.set('view', 'post-signin'); // Update the 'view' parameter
            // Replace the URL without reloading the page
            history.replace({
              pathname: location.pathname,
              search: searchParams.toString(),
            });
          }
          actions.updateSignupDetails({});
        }
      });
  };
  const handleOnSubmit = () => {
    switch (currentState) {
      case rewardSignUpStates.enterNumber:
        if (isEmpty(get(isInvalid, 'phoneNumber', ''))) {
          const brandId = get(appStartupInfo, 'brandId');
          const params = `isRewardOnboarded=true&brandId=${brandId}&phone=${phoneNumber}`;
          actions.beginAjaxCall();
          checkExisitingRewardGuestApi(params).then((response) => {
            if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
              actions.endAjaxCall();
              if (get(response, 'data.innerError.isError')) {
                history.push(`${location.pathname}${location.search}`, 'verify');
                actions.updateSignupDetails({ ...signupDetails, phoneNumber });
                handleGoForward();
                setErrorMessage('');
                handleSendOTP();
              } else {
                setGuestId(get(response, 'data.id', ''));
                history.push(`${location.pathname}${location.search}`, 'verify');
                handleGoForward();
                setErrorMessage('');
                handleSendOTP();
              }
            }
          });
        }
        break;
      case rewardSignUpStates.verifyNumber:
        if (isEmpty(get(isInvalid, 'otp', ''))) {
          const loginReqBody = {
            phoneNumber,
            otp,
            countryCode: constants.defaultCountryCode,
            brandId: get(appStartupInfo, 'brandId', ''),
          };
          actions.attemptLogin(loginReqBody).then((response) => {
            if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
              if (get(response, 'data.guest.isVerified') && !isEmpty(guestId)) {
                // add logic to fetch brand guest detail here
                actions
                  .fetchRewardGuestDetails({
                    guestId,
                    depth: hasTiers,
                  })
                  .then((response) => {
                    if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
                      const brandGuestSignInData = get(response, 'data', {});
                      // Check if igUserId exists in brandGuestSignInData
                      const hasIgUserId = Boolean(get(brandGuestSignInData, 'igUserId', false));
                      if (!hasIgUserId) {
                        const guestId = get(brandGuestSignInData, 'guestId', '');
                        const brandId = get(brandGuestSignInData, 'brandId', '');
                        const requestBody = {
                          guestId,
                          brandId,
                          igUserId,
                          igUserBusinessId,
                          igUserJoiningDate: new Date(),
                        };
                        const withLoader = false;
                        const sendEventToNative = false;
                        actions.updateRewardGuestProfile(
                          requestBody,
                          withLoader,
                          sendEventToNative,
                        );
                      }
                      // Parse existing query parameters
                      const searchParams = new URLSearchParams(location.search);
                      searchParams.set('view', 'post-signin'); // Update the 'view' parameter
                      searchParams.set('isExistingUser', true); // Update the 'isExistingUser' parameter
                      // Replace the URL without reloading the page
                      history.replace({
                        pathname: location.pathname,
                        search: searchParams.toString(),
                      });
                    }
                  });
              } else {
                handleGoForward();
                setShowSignupDetailsPopup(true);
              }
            }
          });
        }
        actions.clearApiErrorResponse();
        break;
      case rewardSignUpStates.enterDetails:
        if (
          (signUpRequiredFields.includes('name') && isEmpty(name)) ||
          (signUpRequiredFields.includes('email') && isEmpty(email)) ||
          (signUpRequiredFields.includes('dob') && isEmpty(birthDate)) ||
          (signUpRequiredFields.includes('doa') && isEmpty(anniversaryDate)) ||
          (signUpRequiredFields.includes('gender') && isEmpty(gender))
        ) {
          if (signUpRequiredFields.includes('name') && isEmpty(name)) {
            toggleFieldInvalidationState({
              name: 'name',
              inValid: true,
              errorMessage: (
                <FormattedMessage
                  id="emptyNameMessage"
                  defaultMessage={resourcesConfig.emptyNameMessage}
                />
              ),
            });
          }

          if (signUpRequiredFields.includes('gender') && isEmpty(gender)) {
            toggleFieldInvalidationState({
              name: 'gender',
              inValid: true,
              errorMessage: (
                <FormattedMessage
                  id="emptyGenderMessage"
                  defaultMessage={resourcesConfig.emptyGenderMessage}
                />
              ),
            });
          }

          if (signUpRequiredFields.includes('email') && isEmpty(email)) {
            toggleFieldInvalidationState({
              name: 'email',
              inValid: true,
              errorMessage: (
                <FormattedMessage
                  id="emptyEmailMessage"
                  defaultMessage={resourcesConfig.emptyEmailMessage}
                />
              ),
            });
          }

          if (signUpRequiredFields.includes('dob') && isEmpty(birthDate)) {
            toggleFieldInvalidationState({
              name: 'birthDate',
              inValid: true,
              errorMessage: (
                <FormattedMessage
                  id="emptyDOBMessage"
                  defaultMessage={resourcesConfig.emptyDOBMessage}
                />
              ),
            });
          }
          if (signUpRequiredFields.includes('doa') && isEmpty(anniversaryDate)) {
            toggleFieldInvalidationState({
              name: 'anniversaryDate',
              inValid: true,
              errorMessage: (
                <FormattedMessage
                  id="emptyAnniversaryDateMessage"
                  defaultMessage={resourcesConfig.emptyAnniversaryDateMessage}
                />
              ),
            });
          }
          return;
        }

        if (Object.values(isInvalid).some((error) => error)) {
          return;
        }
        actions.updateSignupDetails({
          ...signupDetails,
          name,
          email,
          birthDate,
          anniversaryDate,
          gender,
        });
        handleSubmitSignUpAction();
        break;
      default:
        break;
    }
  };

  const handleGoForward = () => {
    setCurrentState(
      {
        [rewardSignUpStates.getStarted]: rewardSignUpStates.enterNumber,
        [rewardSignUpStates.enterNumber]: rewardSignUpStates.verifyNumber,
        [rewardSignUpStates.verifyNumber]: rewardSignUpStates.enterDetails,
      }[currentState],
    );
    setIsInvalid({});
  };

  const handleGoBack = () => {
    switch (currentState) {
      case rewardSignUpStates.verifyNumber:
        history.push(`${location.pathname}${location.search}`, 'sign-up');
        break;
      case rewardSignUpStates.enterDetails:
        history.push(`${location.pathname}${location.search}`, 'verify');
        break;
      default:
        break;
    }

    setCurrentState(
      {
        [rewardSignUpStates.verifyNumber]: rewardSignUpStates.enterNumber,
        [rewardSignUpStates.enterDetails]: rewardSignUpStates.verifyNumber,
      }[currentState],
    );

    setIsInvalid({});
  };

  const renderFooterContent = () =>
    ({
      [rewardSignUpStates.getStarted]: (
        <FormattedMessage
          id="joinRewardClubCTAText"
          defaultMessage={resourcesConfig.joinRewardClubCTAText}
        />
      ),
      [rewardSignUpStates.enterNumber]: (
        <FormattedMessage id="proceedCTAText" defaultMessage={resourcesConfig.proceedCTAText} />
      ),
      [rewardSignUpStates.verifyNumber]: (
        <FormattedMessage id="proceedCTAText" defaultMessage={resourcesConfig.proceedCTAText} />
      ),
      [rewardSignUpStates.enterDetails]: (
        <FormattedMessage
          id="verifyAndUnlockOffer"
          defaultMessage={resourcesConfig.verifyAndUnlockOffer}
        />
      ),
    }[currentState]);

  const renderAttachedLabel = () =>
    resendOtpDuration ? (
      <Countdown
        date={resendOtpDuration}
        renderer={({ minutes, seconds }) => (
          <strong>
            {zeroPad(minutes)}:{zeroPad(seconds)}
          </strong>
        )}
        onComplete={() => setResendOtpDuration(0)}
      />
    ) : (
      <span className="resend-otp" onClick={handleSendOTP}>
        <FormattedMessage id="resendOtp" defaultMessage={resourcesConfig.resendOtp} />
      </span>
    );

  const brandName = get(appStartupInfo, 'brand.name', '');

  const handlePrivacyPolicyLinkClick = (context) => {
    // Use safeDecode to ensure no errors are thrown
    const targetUrl = safeDecode(window.location.href);
    // Encode the target URL once for use as a query parameter
    const encodedTargetUrl = encodeURIComponent(targetUrl);
    handleLinkUrl({
      linkUrl: `${window.location.origin}/${pagesRoutes.pages}/${context}?target=${encodedTargetUrl}`,
    });
  };

  const showPrivacyPolicyLink =
    staticPages &&
    staticPages.length > 0 &&
    staticPages.some(
      (sPage) =>
        sPage.route === pagesRoutes.containers.privacyPolicy ||
        sPage.route === pagesRoutes.containers.termsOfService,
    );

  const renderPrivacyPolicyLink = () =>
    showPrivacyPolicyLink && (
      <div className="user-signup_privacy-policy">
        <FormattedMessage
          id="privacyPolicyAndTermsOfServiceDescription"
          defaultMessage={resourcesConfig.privacyPolicyAndTermsOfServiceDescription}
          values={{
            brand: brandName,
            privacyPolicy: (
              <span
                onClick={() => handlePrivacyPolicyLinkClick(pagesRoutes.containers.privacyPolicy)}
              >
                <FormattedMessage
                  id="privacyPolicy"
                  defaultMessage={resourcesConfig.privacyPolicy}
                />
              </span>
            ),
            termsOfService: (
              <span
                onClick={() => handlePrivacyPolicyLinkClick(pagesRoutes.containers.termsOfService)}
              >
                <FormattedMessage
                  id="termsOfService"
                  defaultMessage={resourcesConfig.termsOfService}
                />
              </span>
            ),
            br: <br />,
          }}
        />
      </div>
    );

  const renderPrivacyPolicyLinkInternal = () => (
    <div className="user-signup_privacy-policy-internal">
      <FormattedMessage
        id="privacyPolicyAndTermsOfServiceDescriptionInternal"
        defaultMessage={resourcesConfig.privacyPolicyAndTermsOfServiceDescriptionInternal}
        values={{
          brand: brandName,
          privacyPolicy: (
            <span
              onClick={() => handlePrivacyPolicyLinkClick(pagesRoutes.containers.privacyPolicy)}
            >
              <FormattedMessage id="privacyPolicy" defaultMessage={resourcesConfig.privacyPolicy} />
            </span>
          ),
          termsOfService: (
            <span
              onClick={() => handlePrivacyPolicyLinkClick(pagesRoutes.containers.termsOfService)}
            >
              <FormattedMessage
                id="termsOfService"
                defaultMessage={resourcesConfig.termsOfService}
              />
            </span>
          ),
          br: <br />,
        }}
      />
    </div>
  );

  return (
    <>
      <div className="user-signup">
        {isEqual(currentState, rewardSignUpStates.getStarted) && (
          <>
            <GetStarted
              appStartupInfo={appStartupInfo}
              showRewardProgramTitle={showRewardProgramTitle}
              handleGoForward={handleGoForward}
              renderFooterContent={renderFooterContent}
              renderPrivacyPolicyLink={renderPrivacyPolicyLink}
              themeData={themeData}
            />
          </>
        )}
        <>
          {!isEmpty(errorMessage) && (
            <div className="user-signup_error-wrapper">
              <ErrorComponent
                errorMessage={errorMessage}
                onCancelErrorComponent={() => setErrorMessage('')}
              />
            </div>
          )}
          <div className="user-signup_deal-image">
            {/* {!isEqual(currentState, rewardSignUpStates.enterNumber) && (
              <div className="user-signup_back">
                <Icons type={'backWhite'} onClick={handleGoBack} />
              </div>
            )} */}
            <img src={dealImg} alt={dealTitle} />
          </div>
          {isEqual(currentState, rewardSignUpStates.enterNumber) && (
            <div className="user-signup_section">
              <div className="section-title">
                <FormattedMessage
                  id="unlockYourReward"
                  defaultMessage={resourcesConfig.unlockYourReward}
                  values={{
                    br: <br />,
                    name: name ? name : 'Hi',
                    displayTitle: dealTitle,
                  }}
                />
              </div>
              <TextField
                className="mt-24 mb-24"
                name="phoneNumber"
                type="text" // Use text to allow spaces and proper formatting
                inputMode="numeric" // Brings up numeric keyboard on mobile
                autoComplete="tel" // Helps browser autofill phone numbers correctly
                placeholder={intl.formatMessage({
                  id: 'phoneNumberPlaceHolder',
                  defaultMessage: resourcesConfig.phoneNumberPlaceHolder,
                })}
                value={phoneNumber}
                autoFocus={true}
                error={!isEmpty(get(isInvalid, 'phoneNumber', ''))}
                helperText={get(isInvalid, 'phoneNumber', '')}
                onBlur={handleOnBlur}
                onChange={handleOnChange}
                onInput={handleOnChange} // Use onInput to catch changes, including autofill
                themeContext={rewardsRoutes.rewards}
              />
              {showPrivacyPolicyLink && (
                <div className="user-signup_section_privacy-policy">
                  {renderPrivacyPolicyLinkInternal()}
                </div>
              )}
              <RecaptchaBranding />
            </div>
          )}
          {isEqual(currentState, rewardSignUpStates.verifyNumber) && (
            <div className="user-signup_section">
              <div className="section-title">
                <FormattedMessage
                  id="otpVerificationTitle"
                  defaultMessage={resourcesConfig.otpVerificationTitle}
                />
              </div>
              <div className="section-description mt-16">
                <FormattedMessage
                  id="enterOtpSentOn"
                  values={{ phoneNumber }}
                  defaultMessage={resourcesConfig.enterOtpSentOn}
                />
                <span className="change-number" onClick={handleGoBack}>
                  <FormattedMessage id="changeCTA" defaultMessage={resourcesConfig.changeCTA} />
                </span>
              </div>
              {showInvalidOtp && (
                <div className="section-error">
                  <FormattedMessage
                    id="invalidOTPMessage"
                    defaultMessage={resourcesConfig.invalidOTPMessage}
                  />
                </div>
              )}
              <TextField
                className="mt-24 mb-24"
                name="otp"
                placeholder={intl.formatMessage({
                  id: 'otpPlaceHolder',
                  defaultMessage: resourcesConfig.otpPlaceHolder,
                })}
                value={otp}
                autoFocus={true}
                error={!isEmpty(get(isInvalid, 'otp', ''))}
                helperText={get(isInvalid, 'otp', '')}
                attachedLabel={renderAttachedLabel()}
                attachedLabelPosition="end"
                onBlur={handleOnBlur}
                onChange={handleOnChange}
                themeContext={rewardsRoutes.rewards}
              />
              {showPrivacyPolicyLink && (
                <div className="user-signup_section_privacy-policy">
                  {renderPrivacyPolicyLinkInternal()}
                </div>
              )}
              <RecaptchaBranding />
            </div>
          )}
          {showSignupDetailsPopup && (
            <SignupDetails
              open={showSignupDetailsPopup}
              name={name}
              anniversaryDate={anniversaryDate}
              birthDate={birthDate}
              email={email}
              gender={gender}
              isAnniversaryTypeReward={isAnniversaryTypeReward}
              isInvalid={isInvalid}
              handleOnBlur={handleOnBlur}
              handleOnChange={handleOnChange}
              signUpHiddenFields={signUpHiddenFields}
              showUserInfoFields={showUserInfoFields}
              genderFields={genderFields}
              handleOnSubmit={handleOnSubmit}
              dealTitle={dealTitle}
            />
          )}
          <InvisibleRecaptcha recaptchaRef={recaptchaRef} />
          <div className="user-signup_footer action-btn">
            <Button
              content={renderFooterContent()}
              themeContext={rewardsRoutes.rewards}
              onClick={handleOnSubmit}
            />
          </div>
        </>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  signupDetails: get(state, 'rewards.signupDetails', {}),
  guestDetails: get(state, 'authentication.loginData', {}),
  appStartupInfo: get(state, 'common.appStartupInfo', {}),
  errorResponse: get(state, 'common.errorResponse', {}),
  callbackUrl: get(state, 'rewards.callbackUrl', ''),
  showRewardProgramTitle: get(state, 'common.resources.showRewardProgramTitle', true),
  themeData: get(state, 'common.resources.themeData', {}),
  staticPages: get(state, 'common.resources.staticPages', []),
  rewardProgram: get(state, 'common.appStartupInfo.rewardProgram', {}),
  signUpRequiredFields: get(
    state,
    'common.resources.signUpRequiredFields',
    resourcesConfig.signUpRequiredFields,
  ),
  signUpHiddenFields: get(state, 'common.resources.signUpHiddenFields', []),
  socialMediaCampaignDetails: get(state, 'common.socialMediaCampaignDetails', {}),
  genderFields: get(state, 'common.resources.genderFields', resourcesConfig.genderFields),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      sendOtp: sendOtpAction,
      attemptLogin: attemptLoginAction,
      updateRewardGuestProfile: updateRewardGuestProfileAction,
      clearApiErrorResponse: clearApiErrorResponseAction,
      beginAjaxCall: beginAjaxCallAction,
      endAjaxCall: endAjaxCallAction,
      dispatchCallbackUrl: dispatchCallbackUrlAction,
      updateSignupDetails: updateSignupDetailsAction,
      fetchRewardGuestDetails: fetchRewardGuestDetailsAction,
      fetchDealById: fetchDealByIdAction,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(Signup);
